import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api from "../lib/api";
import { Check, ArrowLeft, Star, Loader2, Gift, ShieldCheck, LifeBuoy, Lock, AlertTriangle } from "lucide-react";
import { useCurrency } from "../context/CurrencyContext";
import CurrencySwitcher from "../components/CurrencySwitcher";
import { pricingPlans } from "../mock";

const TIER_META = {
  petite: { name: "Petite", tag: "Intimate gatherings", popular: false, idx: 0 },
  classic: { name: "Classic", tag: "Most popular", popular: true, idx: 1 },
  grand: { name: "Grand", tag: "Large celebrations", popular: false, idx: 2 },
  kids: { name: "Kids Party", tag: "Cake, chaos, capes", popular: false, idx: 0 },
};

const featureList = (tierKey) => {
  const plan = pricingPlans.wedding?.[TIER_META[tierKey].idx];
  return plan?.features || [];
};

const EventUpgradePage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { currency, format } = useCurrency();
  const [tiers, setTiers] = useState(null);
  const [event, setEvent] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [credits, setCredits] = useState({});
  const [busy, setBusy] = useState("");
  const [polling, setPolling] = useState(false);
  const [result, setResult] = useState("");
  const [loadError, setLoadError] = useState("");
  const [liveMode, setLiveMode] = useState(true);

  const refreshCredits = useCallback(async () => {
    try {
      const r = await api.get("/users/me/credits");
      setCredits(r.data.credits || {});
    } catch (err) { console.debug("[upgrade] credits fetch skipped:", err?.message); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    (async () => {
      try {
        const [t, e] = await Promise.all([
          api.get(`/payments/tiers?currency=${currency}`),
          api.get(`/events/${id}`),
        ]);
        if (cancelled) return;
        setTiers(t.data);
        setEvent(e.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        // eslint-disable-next-line no-console
        console.error("[upgrade] load failed", status, detail, err);
        if (status === 401) setLoadError("Your session expired. Please sign in again.");
        else if (status === 404) setLoadError("Event not found or you don't have access to it.");
        else setLoadError(detail || "Could not load upgrade options. Please refresh or try again.");
      }
    })();
    api.get("/referrals/me").then((r) => !cancelled && setRewards(r.data.rewards)).catch(() => {});
    api.get("/payments/mode").then((r) => !cancelled && setLiveMode(r.data?.livemode !== false)).catch(() => {});
    refreshCredits();
    return () => { cancelled = true; };
  }, [id, currency, refreshCredits]);

  // Handle return from Stripe or Paystack: poll payment status until confirmed/expired.
  // Stripe adds `?session_id=cs_...`; Paystack adds `?reference=gc_...` (and `trxref`).
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    const paystackRef = params.get("reference") || params.get("trxref");
    if (!sid && !paystackRef) return;
    setPolling(true);
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = paystackRef
          ? await api.get(`/paystack/verify/${paystackRef}`)
          : await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setResult("success"); setPolling(false);
          setTimeout(() => nav(`/dashboard/events/${id}`), 2200);
          return;
        }
        if (r.data.status === "expired") { setResult("expired"); setPolling(false); return; }
        if (attempts < 8) setTimeout(poll, 2000);
        else { setResult("timeout"); setPolling(false); }
      } catch {
        if (cancelled) return;
        if (attempts < 8) setTimeout(poll, 2000);
        else { setResult("error"); setPolling(false); }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [loc.search, id, nav]);

  const upgrade = async (tierKey) => {
    setBusy(tierKey);
    try {
      // Currency-based gateway routing. Paystack handles NGN/GHS/ZAR/KES;
      // Stripe handles everything else.
      const usePaystack = ["NGN", "GHS", "ZAR", "KES"].includes((currency || "").toUpperCase());
      const endpoint = usePaystack ? "/paystack/checkout" : "/payments/checkout";
      const payload = usePaystack
        ? {
            purchase_type: "event_upgrade",
            event_id: id, tier: tierKey, currency,
            origin_url: window.location.origin,
          }
        : { event_id: id, tier: tierKey, currency, origin_url: window.location.origin };
      const res = await api.post(endpoint, payload);
      const url = res?.data?.authorization_url || res?.data?.url;
      if (!url) {
        throw new Error(
          usePaystack
            ? "Paystack did not return a checkout URL. Please retry."
            : "Stripe did not return a checkout URL. Please retry.",
        );
      }
      const safety = setTimeout(() => setBusy(""), 8000);
      window.location.assign(url);
      void safety;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[upgrade] checkout failed", e);
      alert(e?.response?.data?.detail || e?.message || "Could not start checkout. Please try again.");
      setBusy("");
    }
  };

  const redeemCredit = async (tierKey) => {
    setBusy(`credit-${tierKey}`);
    try {
      const res = await api.post(`/events/${id}/use-credit?tier=${tierKey}`);
      alert(`Upgraded to ${tierKey} using 1 credit! Redirecting to your event…`);
      // Reflect the new tier locally + navigate.
      setBusy("");
      nav(`/dashboard/events/${id}`);
      void res;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not consume credit. Please try again.");
      setBusy("");
      refreshCredits();
    }
  };

  if (polling || result) {
    return (
      <section className="min-h-[80vh] bg-cream-100 flex items-center justify-center">
        <div className="bg-white rounded-3xl soft-shadow p-10 text-center max-w-md">
          {polling && (
            <>
              <Loader2 className="w-10 h-10 animate-spin text-coral-300 mx-auto" />
              <h2 className="font-serif italic text-navy-700 text-3xl mt-5">Confirming payment...</h2>
              <p className="text-navy-700/60 mt-2">Hang tight, this usually takes a few seconds.</p>
            </>
          )}
          {result === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check className="w-8 h-8" /></div>
              <h2 className="font-serif italic text-navy-700 text-3xl mt-5">Payment confirmed!</h2>
              <p className="text-navy-700/60 mt-2">Your gallery has been upgraded. Redirecting...</p>
            </>
          )}
          {result === "expired" && <p className="text-red-600">Checkout session expired. Please try again.</p>}
          {result === "timeout" && <p className="text-amber-600">Still processing. Refresh in a minute to confirm.</p>}
          {result === "error" && <p className="text-red-600">Could not verify payment. Contact support.</p>}
        </div>
      </section>
    );
  }

  if (!tiers || !event) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5">
        {loadError ? (
          <div data-testid="upgrade-load-error" className="bg-white rounded-2xl soft-shadow p-8 max-w-md text-center space-y-4">
            <h2 className="font-serif italic text-navy-700 text-2xl">We couldn&apos;t load your upgrade options</h2>
            <p className="text-navy-700/70 text-sm">{loadError}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                data-testid="upgrade-retry-btn"
                className="bg-coral-300 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-coral-400"
              >
                Try again
              </button>
              <Link
                to={`/dashboard/events/${id}`}
                className="border-2 border-navy-700 text-navy-700 px-5 py-2.5 rounded-full font-semibold hover:bg-navy-700 hover:text-white"
              >
                Back to event
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="upgrade-loading" className="text-navy-700/60 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        )}
      </div>
    );
  }

  const hasDiscount = rewards?.available > 0;

  return (
    <section className="min-h-[80vh] bg-cream-100 py-12">
      <div className="max-w-5xl mx-auto px-5">
        {!liveMode && (
          <div data-testid="upgrade-testmode-banner" className="mb-6 rounded-2xl bg-red-50 border-2 border-red-300 text-red-800 px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-sm">Stripe is currently in TEST mode</div>
              <p className="text-sm mt-0.5">Real cards will NOT be charged. Only Stripe test cards (e.g. <code>4242 4242 4242 4242</code>) will work here. Contact your admin to switch <code>STRIPE_API_KEY</code> to a live secret before accepting real payments.</p>
            </div>
          </div>
        )}
        <Link to={`/dashboard/events/${id}`} className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Back to event</Link>
        {(() => {
          const isRenew = new URLSearchParams(loc.search).get("mode") === "renew";
          const exp = event.expires_at ? new Date(event.expires_at) : null;
          const expired = exp && exp.getTime() <= Date.now();
          return (
            <div className="text-center mb-10">
              <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">
                {isRenew ? (expired ? "Renew Expired Gallery" : "Renew Gallery") : "Upgrade Gallery"}
              </p>
              <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">
                {isRenew
                  ? <>Renew your <span className="italic">gallery</span></>
                  : <>Choose your <span className="italic">plan</span></>}
              </h1>
              <p className="text-navy-700/60 mt-3">
                Currently on <span className="font-semibold capitalize">{tiers[event.tier]?.label || "Free"}</span> tier
                {isRenew && !expired && " — any remaining days get added to your new window"}
                {isRenew && expired && " — your gallery is currently offline"}.
              </p>
              <div className="mt-5 inline-flex items-center gap-3 bg-white rounded-full px-4 py-2 soft-shadow">
                <span className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Currency</span>
                <CurrencySwitcher compact />
              </div>
              {hasDiscount && (
            <div data-testid="referral-discount-banner" className="mt-5 max-w-lg mx-auto flex items-center justify-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm font-semibold">
              <Gift className="w-4 h-4 shrink-0" />
              Referral reward unlocked — {rewards.discount_pct}% off will be applied automatically at checkout!
            </div>
          )}
        </div>
          );
        })()}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {["petite", "classic", "grand", "kids"].map((k) => {
            const t = tiers[k];
            const meta = TIER_META[k];
            return (
              <div key={k} className={`relative rounded-2xl p-7 bg-white border ${meta.popular ? "border-coral-300 soft-shadow-coral" : "border-cream-300/60 soft-shadow"}`}>
                {meta.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral-300 text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> {meta.tag}
                  </div>
                )}
                <h3 className="font-serif italic text-2xl text-navy-700">{meta.name}</h3>
                <p className="text-sm text-navy-700/60 mt-1">{!meta.popular ? meta.tag : "For your special day"}</p>
                <div className="mt-5 flex items-baseline gap-2">
                  {hasDiscount ? (
                    <>
                      <span className="text-2xl font-serif text-navy-700/40 line-through">{format(t.price)}</span>
                      <span data-testid={`discounted-price-${k}`} className="text-5xl font-serif text-navy-700">{format(Math.round(t.price * (1 - rewards.discount_pct / 100) * 100) / 100)}</span>
                      <span className="text-sm text-navy-700/60">one-time</span>
                    </>
                  ) : (
                    <>
                      <span className="text-5xl font-serif text-navy-700">{format(t.price)}</span>
                      <span className="text-sm text-navy-700/60">one-time</span>
                    </>
                  )}
                </div>
                <ul className="mt-6 space-y-3">
                  {(t.features && t.features.length ? t.features : featureList(k)).map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-navy-700/80"><Check className="w-4 h-4 text-coral-300 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                {credits[k] > 0 && event.tier !== k && (
                  <button
                    onClick={() => redeemCredit(k)}
                    disabled={busy === `credit-${k}`}
                    data-testid={`use-credit-${k}`}
                    className="mt-6 w-full text-center py-3 rounded-full font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    {busy === `credit-${k}`
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying credit…</>
                      : <><Gift className="w-4 h-4" /> Use 1 {meta.name} credit — free ({credits[k]} available)</>}
                  </button>
                )}
                <button
                  onClick={() => upgrade(k)}
                  disabled={busy === k || event.tier === k}
                  data-testid={`stripe-upgrade-${k}`}
                  className={`mt-3 w-full text-center py-3 rounded-full font-semibold transition-colors ${meta.popular ? "bg-coral-300 text-white hover:bg-coral-400" : "border-2 border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white"} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2`}
                >
                  {busy === k ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : event.tier === k ? "Current plan" : `Upgrade to ${meta.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-navy-700/50 mt-8">Secure payments by Stripe. One-time charge per event — no subscription.</p>

        <div data-testid="upgrade-trust-strip" className="mt-6 max-w-4xl mx-auto grid sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl soft-shadow p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-coral-400 mt-0.5" />
            <div>
              <div className="font-semibold text-navy-700 text-sm">14-day money-back guarantee</div>
              <p className="text-xs text-navy-700/60 mt-0.5">Not delighted? Full refund, no questions asked.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl soft-shadow p-4 flex items-start gap-3">
            <LifeBuoy className="w-5 h-5 text-coral-400 mt-0.5" />
            <div>
              <div className="font-semibold text-navy-700 text-sm">7-day real customer care</div>
              <p className="text-xs text-navy-700/60 mt-0.5">Real humans, every day of the week.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl soft-shadow p-4 flex items-start gap-3">
            <Lock className="w-5 h-5 text-coral-400 mt-0.5" />
            <div>
              <div className="font-semibold text-navy-700 text-sm">Enterprise-grade security</div>
              <p className="text-xs text-navy-700/60 mt-0.5">Encrypted at rest &amp; in transit. Your copyright stays with you.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventUpgradePage;
